import React, { useEffect, useState, Component } from 'react';
import { ArrowRight, TrendingUp, Users, Heart, MessageCircle, Target, Zap, Star, Activity, AlertTriangle, Lightbulb, Brain, TrendingDown, CornerRightUp, CornerRightDown, Sparkles } from 'lucide-react';
// Prompt type: 'revele' for insights/data, 'propose' for suggestions/actions
type PromptType = 'revele' | 'propose';
interface Prompt {
  text: string;
  type: PromptType;
}
const PROMPTS: Prompt[] = [{
  text: "L'épuisement de Louis génère des erreurs d'étiquetage répétées en magasin.",
  type: 'revele'
}, {
  text: 'Réorganisez le planning de Louis pour préserver sa lucidité opérationnelle.',
  type: 'propose'
}, {
  text: 'Marie craint de ne pas évoluer, limitant son initiative sur de nouvelles tâches.',
  type: 'revele'
}, {
  text: "Identifiez les freins de Marie pour restaurer sa présence et l'équilibre collectif.",
  type: 'propose'
}, {
  text: 'Thomas manque de reconnaissance, ce qui dégrade la qualité de son accueil client.',
  type: 'revele'
}, {
  text: 'Félicitez Thomas plus souvent pour dynamiser son excellence relationnelle.',
  type: 'propose'
}, {
  text: "Milo bâcle l'inventaire car elle juge cette tâche inutile et mécanique.",
  type: 'revele'
}, {
  text: "Expliquez à Milo l'impact financier d'un stock juste pour fiabiliser ses saisies.",
  type: 'propose'
}];
// Avatar URLs for each person
const AVATARS: Record<string, string> = {
  Marie: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
  Louis: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
  Thomas: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
  Sophie: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
  Milo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face'
};
// Recognition Stats Card Component
function RecognitionCard({
  person,
  percentage,
  trend
}: {
  person: string;
  percentage: number;
  trend: number;
}) {
  const avatarUrl = AVATARS[person] || AVATARS.Marie;
  return <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 max-w-[280px] border border-white/30 animate-fade-in" data-id="element-1">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3" data-id="element-2">
        <img src={avatarUrl} alt={person} className="w-10 h-10 rounded-full object-cover border-2 border-rewayz-surface3" data-id="element-3" />
        <div data-id="element-4">
          <p className="text-rewayz-navy text-sm font-semibold" data-id="element-5">{person}</p>
          <p className="text-rewayz-text-secondary text-xs" data-id="element-6">Reconnaissance</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-end justify-between" data-id="element-7">
        <div data-id="element-8">
          <p className="text-3xl font-bold text-rewayz-navy" data-id="element-9">{percentage}%</p>
          <p className="text-xs text-rewayz-text-muted" data-id="element-10">sur 3 mois</p>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-rewayz-success/10" data-id="element-11">
          <TrendingUp className="w-3.5 h-3.5 text-rewayz-success" data-id="element-12" />
          <span className="text-xs font-semibold text-rewayz-success" data-id="element-13">
            +{trend}%
          </span>
        </div>
      </div>

      {/* Mini chart visualization */}
      <div className="mt-3 flex items-end gap-1 h-8" data-id="element-14">
        <div className="flex-1 bg-rewayz-surface2 rounded-sm h-4" data-id="element-15"></div>
        <div className="flex-1 bg-rewayz-surface2 rounded-sm h-5" data-id="element-16"></div>
        <div className="flex-1 bg-rewayz-violet/60 rounded-sm h-7" data-id="element-17"></div>
        <div className="flex-1 bg-rewayz-violet rounded-sm h-8" data-id="element-18"></div>
      </div>
    </div>;
}
// Floating Icon Component
function FloatingIcon({
  icon: Icon,
  className = '',
  size = 'md'
}: {
  icon: React.ElementType;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClasses = {
    sm: 'w-8 h-8 p-1.5',
    md: 'w-12 h-12 p-2.5',
    lg: 'w-16 h-16 p-3.5'
  };
  return <div className={`rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 ${sizeClasses[size]} ${className}`} data-id="element-19">
      <Icon className="w-full h-full text-white/40" strokeWidth={1.5} data-id="element-20" />
    </div>;
}
// Avatar component for inline display
function InlineAvatar({
  name
}: {
  name: string;
}) {
  const avatarUrl = AVATARS[name] || AVATARS.Thomas;
  return <img src={avatarUrl} alt={name} className="inline-block w-8 h-8 lg:w-10 lg:h-10 rounded-full object-cover mx-1 align-middle border-2 border-purple-300/50" data-id="element-21" />;
}
// Inline Badge Component for highlighted terms
function InlineBadge({
  icon: Icon,
  children
}: {
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-rewayz-amber/40 bg-rewayz-amber/10 mx-2 my-1" data-id="element-22">
      <Icon className="w-3 h-3 text-rewayz-amber flex-shrink-0" data-id="element-23" />
      <span className="text-white text-sm font-medium whitespace-nowrap" data-id="element-24">
        {children}
      </span>
    </span>;
}
// Sparkle burst animation component
function SparkleEffect({
  isActive
}: {
  isActive: boolean;
}) {
  if (!isActive) return null;
  return <span className="relative inline-flex items-center ml-1" data-id="element-25">
      <Sparkles className="w-4 h-4 text-rewayz-amber animate-ping absolute" strokeWidth={2} data-id="element-26" />
      <Sparkles className="w-4 h-4 text-rewayz-amber animate-pulse" strokeWidth={2} data-id="element-27" />
    </span>;
}
export function HeroSection() {
  const [text, setText] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [delta, setDelta] = useState(100);
  const [showProposeEffect, setShowProposeEffect] = useState(false);
  const [prevPromptType, setPrevPromptType] = useState<PromptType>('revele');
  useEffect(() => {
    let ticker = setInterval(() => {
      tick();
    }, delta);
    return () => clearInterval(ticker);
  }, [text, delta]);
  const tick = () => {
    let i = promptIndex % PROMPTS.length;
    let fullText = PROMPTS[i].text;
    let updatedText = isDeleting ? fullText.substring(0, text.length - 1) : fullText.substring(0, text.length + 1);
    setText(updatedText);
    if (isDeleting) {
      setDelta(50);
    }
    if (!isDeleting && updatedText === fullText) {
      setIsDeleting(true);
      setDelta(2000);
    } else if (isDeleting && updatedText === '') {
      setIsDeleting(false);
      setPromptIndex(promptIndex + 1);
      setDelta(100);
    } else if (!isDeleting && updatedText !== fullText) {
      setDelta(50 + Math.random() * 50);
    }
  };
  // Get current prompt type
  const currentPromptType = PROMPTS[promptIndex % PROMPTS.length].type;
  // Detect when verb changes to "propose" and trigger effects
  useEffect(() => {
    if (currentPromptType !== prevPromptType) {
      setPrevPromptType(currentPromptType);
      // Only show effects when switching TO "propose"
      if (currentPromptType === 'propose') {
        setShowProposeEffect(true);
        const timer = setTimeout(() => setShowProposeEffect(false), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [currentPromptType, prevPromptType]);
  // Render text with avatar before names
  const renderTextWithAvatar = () => {
    const names = ['Marie', 'Louis', 'Thomas', 'Sophie', 'Milo'];
    for (const name of names) {
      const nameIndex = text.indexOf(name);
      if (nameIndex !== -1) {
        const beforeName = text.substring(0, nameIndex);
        const nameAndAfter = text.substring(nameIndex);
        const nameOnly = nameAndAfter.substring(0, name.length);
        const afterName = nameAndAfter.substring(name.length);
        return <>
            {beforeName}
            <InlineAvatar name={name} data-id="element-28" />
            <span className="text-rewayz-yellow" data-id="element-29">{nameOnly}</span>
            {afterName}
          </>;
      }
    }
    return text;
  };
  return <section className="relative w-full min-h-screen flex flex-col lg:flex-row overflow-hidden" data-id="element-30">
      {/* Left Side - Navy Background with Typewriter */}
      <div className="w-full lg:w-[65%] bg-rewayz-navy flex flex-col justify-center px-8 lg:px-16 py-20 relative overflow-hidden" data-id="element-31">
        {/* Decorative gradient overlay - BEHIND illustrations */}
        <div className="absolute inset-0 bg-gradient-to-br from-rewayz-navy via-rewayz-navy/95 to-rewayz-navy/80 pointer-events-none z-0" data-id="element-32" />

        {/* Decorative Floating Illustrations - ABOVE gradient */}
        <div className="hidden lg:block absolute inset-0 pointer-events-none z-[5]" data-id="element-33">
          {/* Top right area */}
          <FloatingIcon icon={Users} size="lg" className="absolute top-20 right-16 transform rotate-12" data-id="element-34" />
          <FloatingIcon icon={Heart} size="md" className="absolute top-32 right-48 transform -rotate-6" data-id="element-35" />

          {/* Brain - prominent position */}
          <FloatingIcon icon={Brain} size="lg" className="absolute top-24 right-36 transform rotate-3" data-id="element-36" />

          {/* Middle right */}
          <FloatingIcon icon={MessageCircle} size="md" className="absolute top-1/3 right-12 transform rotate-6" data-id="element-37" />
          <FloatingIcon icon={Target} size="sm" className="absolute top-1/2 right-32 transform -rotate-12" data-id="element-38" />

          {/* Curved arrows - trending up */}
          <FloatingIcon icon={TrendingUp} size="md" className="absolute top-[45%] right-8 transform rotate-6" data-id="element-39" />
          <FloatingIcon icon={CornerRightUp} size="sm" className="absolute top-[38%] right-44 transform -rotate-3" data-id="element-40" />

          {/* Bottom right */}
          <FloatingIcon icon={Zap} size="lg" className="absolute bottom-40 right-20 transform rotate-3" data-id="element-41" />
          <FloatingIcon icon={Star} size="sm" className="absolute bottom-28 right-48 transform -rotate-6" data-id="element-42" />

          {/* Curved arrows - trending down */}
          <FloatingIcon icon={TrendingDown} size="sm" className="absolute bottom-52 right-12 transform rotate-12" data-id="element-43" />
          <FloatingIcon icon={CornerRightDown} size="md" className="absolute bottom-24 right-32 transform -rotate-6" data-id="element-44" />

          {/* Left side (partially visible) */}
          <FloatingIcon icon={Heart} size="sm" className="absolute top-28 left-8 transform -rotate-12" data-id="element-45" />
          <FloatingIcon icon={Users} size="md" className="absolute bottom-36 left-4 transform rotate-12" data-id="element-46" />
          <FloatingIcon icon={Brain} size="sm" className="absolute top-1/2 left-6 transform rotate-6" data-id="element-47" />
          <FloatingIcon icon={TrendingUp} size="sm" className="absolute bottom-48 left-12 transform -rotate-3" data-id="element-48" />

          {/* Decorative circles */}
          <div className="absolute top-1/4 right-8 w-32 h-32 rounded-full border-2 border-white/10" data-id="element-49" />
          <div className="absolute bottom-1/4 right-24 w-48 h-48 rounded-full border-2 border-white/10" data-id="element-50" />
          <div className="absolute top-1/2 right-40 w-24 h-24 rounded-full bg-rewayz-violet/10" data-id="element-51" />
        </div>

        {/* Main Content */}
        <div className="max-w-xl mx-auto w-full relative z-10 flex flex-col h-full justify-between py-8" data-id="element-52">
          {/* Top section - Logo phrase and typewriter */}
          <div data-id="element-53">
            {/* Dynamic phrase with Logo */}
            <div className="flex items-center gap-2 mb-6 flex-wrap" data-id="element-54">
              <span className="text-white/70 text-lg font-medium" data-id="element-55">
                Voilà ce que
              </span>
              <img src="https://cdn.magicpatterns.com/uploads/vvKPgh6ZyT6sqTxcDq83VU/logo_rewayz_white.png" alt="Rewayz" className="h-6 w-auto" data-id="element-56" />
              <span className="inline-flex items-center" data-id="element-57">
                <span key={currentPromptType} className={`text-lg font-semibold transition-all duration-300 ${currentPromptType === 'revele' ? 'text-white' : 'text-rewayz-yellow'}`} data-id="element-58">
                  {currentPromptType === 'revele' ? 'révèle' : 'propose'}
                </span>
                <SparkleEffect isActive={showProposeEffect && currentPromptType === 'propose'} data-id="element-59" />
              </span>
            </div>

            <div className="min-h-[120px]" data-id="element-60">
              <h1 className="text-3xl lg:text-4xl font-semibold text-white leading-tight" data-id="element-61">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-300 to-pink-300" data-id="element-62">
                  {renderTextWithAvatar()}
                </span>
                <span className="animate-pulse text-rewayz-violet ml-1" data-id="element-63">|</span>
              </h1>
            </div>
          </div>

          {/* Bottom section - Features and logos */}
          <div className="max-w-md pb-8" data-id="element-64">
            <p className="text-white/80 text-sm mb-3" data-id="element-65">
              L'outil intelligent{' '}
              <span className="font-semibold text-white" data-id="element-66">
                pour les managers
              </span>{' '}
              qui :
            </p>
            <div className="space-y-2" data-id="element-67">
              <div className="flex items-center gap-2" data-id="element-68">
                <Activity className="w-4 h-4 text-rewayz-amber" data-id="element-69" />
                <span className="text-white text-sm" data-id="element-70">
                  Révèle{' '}
                  <span className="text-rewayz-amber font-medium" data-id="element-71">
                    l'engagement réel
                  </span>{' '}
                  de vos équipes
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-72">
                <AlertTriangle className="w-4 h-4 text-rewayz-amber" data-id="element-73" />
                <span className="text-white text-sm" data-id="element-74">
                  Identifie leurs{' '}
                  <span className="text-rewayz-amber font-medium" data-id="element-75">
                    points de friction
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-76">
                <Lightbulb className="w-4 h-4 text-rewayz-amber" data-id="element-77" />
                <span className="text-white text-sm" data-id="element-78">
                  Vous guide vers les{' '}
                  <span className="text-rewayz-amber font-medium" data-id="element-79">
                    bonnes décisions
                  </span>
                </span>
              </div>
            </div>

            {/* Trust logos section */}
            <div className="mt-8 pt-6 border-t border-white/10" data-id="element-80">
              <p className="text-white/50 text-xs mb-4 uppercase tracking-wide" data-id="element-81">
                Les managers qui nous font déjà confiance
              </p>
              <div className="flex items-center gap-4" data-id="element-82">
                <img src="https://cdn.magicpatterns.com/uploads/qVDayf3WHpJVRoWeG6VnbA/logo-leclerc.png" alt="E.Leclerc" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" data-id="element-83" />
                <img src="https://cdn.magicpatterns.com/uploads/6TU12NpPSmENwdsNKo2iGC/logo-french-bastard.png" alt="French Bastards" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" data-id="element-84" />
                <img src="https://cdn.magicpatterns.com/uploads/c5QCxaiYjctKswYq1rqRNo/image_(16)_1.png" alt="TotalEnergies" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" data-id="element-85" />
                <img src="https://cdn.magicpatterns.com/uploads/8cCEC2Li7ij35LMq56TwtS/image_(17)_1.png" alt="Qualimétrie" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" data-id="element-86" />
                <img src="https://cdn.magicpatterns.com/uploads/iHdDAHqBN27KuEuJDEgU2b/image_(22)_1.png" alt="Burger King" className="h-6 w-auto opacity-70 hover:opacity-100 transition-opacity" data-id="element-87" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-rewayz-navy to-transparent pointer-events-none z-20" data-id="element-88" />
      </div>

      {/* Right Side - Black/Dark Background with Actions */}
      <div className="w-full lg:w-[35%] bg-black flex flex-col justify-center items-center px-8 lg:px-16 py-20 relative" data-id="element-89">
        <div className="max-w-md w-full text-center lg:text-left" data-id="element-90">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12 text-center" data-id="element-91">
            Démarrer
          </h2>

          <div className="flex flex-col gap-2 justify-center items-center w-full mb-8" data-id="element-92">
            <button className="w-full sm:w-auto px-8 py-3 rounded-btn bg-rewayz-violet text-white font-semibold hover:bg-blue-600 transition-colors focus:ring-2 focus:ring-rewayz-violet focus:ring-offset-2 focus:ring-offset-black" data-id="element-93">
              Inscription gratuite
            </button>
            <p className="text-rewayz-surface3/60 text-xs" data-id="element-94">
              Gratuit pour une équipe · Sans carte bancaire
            </p>
          </div>

          <div className="text-center" data-id="element-95">
            <a href="#" className="text-rewayz-surface3/80 hover:text-white transition-colors text-sm flex items-center justify-center gap-2 group" data-id="element-96">
              Essayez-le d'abord
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-97" />
            </a>
          </div>

          {/* Legal links */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4" data-id="element-98">
            <a href="#" className="text-rewayz-surface3/50 hover:text-rewayz-surface3/80 transition-colors text-xs" data-id="element-99">
              Conditions d'utilisation
            </a>
            <span className="text-rewayz-surface3/30" data-id="element-100">•</span>
            <a href="#" className="text-rewayz-surface3/50 hover:text-rewayz-surface3/80 transition-colors text-xs" data-id="element-101">
              Politique de confidentialité
            </a>
          </div>
        </div>
      </div>
    </section>;
}